<template>
  <div class="tabs">
    <div class="tab-bar-scroll-box">
      <div class="tab-bar">
        <draggable
          v-model="tabs"
          class="draggable"
          v-bind="dragOptions"
          @start="drag = true"
          @end="refreshTabs">
          <transition-group
            type="transition"
            :name="!drag ? 'flip-list' : null"
            class="draggable">
            <div v-for="tab in tabs"
                 :key="tab.id"
                 class="tab"
                 :class="{active: tab.id === activeTabID, editable}"
                 @mousedown="switchTab(tab.id)">
              <slot name="tab" :label="tab.label">
                <span ref="tab-label" class="label">{{tab.label}}</span>
              </slot>
              <div v-show="removable && tabs.length > 1" class="tab-editor">
                <button v-if="tab.id === activeTabID" class="secondary">
                  <i class="close-small" @click="removeTab"/>
                </button>
              </div>
            </div>
          </transition-group>
        </draggable>
        <div v-if="editable" class="tab plus">
          <i class="plus" :title="plusButtonTitle" @click.prevent="addTab"/>
        </div>
      </div>
    </div>
    <div ref="content" class="tab-content">
      <slot name="tab-content" :activeTabID="activeTabID"/>
    </div>
  </div>
</template>

<script>
import Draggable from 'vuedraggable'
import smoothHeight from 'vue-smooth-height'

export default {
  name: 'Tabs',
  mixins: [smoothHeight],
  components: { Draggable },
  props: {
    value: { type: Array, default: () => [] },
    default: { type: [String, Number], default: null },
    editable: Boolean,
    draggable: Boolean,
    removable: Boolean,
    plusButtonTitle: { type: String, default: 'Add Tab' }
  },

  data: () => ({
    currentTabID: null,
    drag: false
  }),

  computed: {
    tabs: {
      get () {
        if (this.valueIsArrayOfPrimitives) {
          return this.value.map((label, id) => ({ label, id }))
        }
        return this.value
      },
      set (value) {
        if (this.valueIsArrayOfPrimitives) {
          value = value.map(item => item.label)
        }
        this.$emit('input', value)
      }
    },

    activeTabID () {
      if (!this.value.length) return null
      return this.currentTabID || this.default || this.tabs[0].id
    },

    valueIsArrayOfPrimitives () {
      return this.value.length && (typeof this.value[0] === 'string' || typeof this.value[0] === 'number')
    },

    dragOptions () {
      return {
        animation: 200,
        group: 'tabs',
        disabled: false,
        ghostClass: 'ghost'
      }
    }
  },

  mounted () {
    this.$smoothElement({
      el: this.$refs.content,
      transition: 'height .1s'
    })
  },

  methods: {
    switchTab (id) {
      this.passiveSwitchTab(id)
      this.$emit('switchTab', id)
    },

    refreshTabs () {
      this.drag = false
      this.$emit('switchTab', this.currentTabID)
    },

    passiveSwitchTab (id) {
      this.currentTabID = id
    },

    addTab () {
      this.$emit('add')
    },

    removeTab () {
      this.$emit('remove')
    }
  }
}
</script>

<style lang="scss">
  @import "../../../scss/vars";

  $active-color: $bg-secondary-color;
  .tabs {
    .tab-bar-scroll-box {
      position: relative;
      width: 100%;
      overflow: auto;
      padding-bottom: $basic-padding;

      .tab-bar {
        position: relative;
        min-width: min-content;
        display: flex;
        align-items: center;

        &:after {
          content: '';
          position: absolute;
          width: 100%;
          left: 0;
          top: 100%;
          height: $basic-padding;
          background: $active-color;
        }

        .draggable {
          min-width: min-content;
          display: flex;
          align-items: center;
        }

        .tab {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          height: $basic-size;
          padding: 0 $basic-padding;
          min-width: $basic-size * 1.5;
          max-width: $basic-size * 5;
          font-size: $font-size-caption;
          color: $font-color-inactive;
          cursor: pointer;

          &.compact {
            height: $three-quarters;
          }

          .label {
            padding: $small-padding $basic-padding;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }

          .tab-editor {
            display: flex;
            align-items: center;
            width: 0;
            transition: width 0.15s;
          }

          &.plus {
            min-width: $basic-size;
          }

          &.active {
            background: $active-color;
            color: $font-color;

            &.editable {
              /*padding: 0;*/
            }

            .tab-editor {
              width: $basic-size;
            }
          }
        }
      }
    }

    .tab-content {
      overflow: hidden;
      background: $active-color;
    }

    .flip-list-move {
      transition: transform 0.5s;
    }

    .no-move {
      transition: transform 0s;
    }

    .ghost {
      opacity: 0.5;
      background: #c8ebfb;
    }
  }
</style>
